import httpClient from '../axios/interceptors'
import { API } from './endpoint'
import { handleResponseError } from 'helpers/utils'
import { AxiosResponse } from 'axios'

// --------------------------------------------------------
// Types
// --------------------------------------------------------

export interface SpeedMerchantItem {
  id: string
  factory_id: string
  is_active: boolean
  is_default: boolean
  is_delete: boolean
  speed_name: string
  pickup_name: string
  address: string
  county: string   // sub_district_name
  city: string     // district_name
  state: string    // province_name
  postcode: string
  mobile1: string
  email: string
  province_id: number
  district_id: number
  sub_district_id: number
  code: string
}

export interface GetMerchantListResponse {
  status: string
  data: SpeedMerchantItem[]
}

export interface ReplaceDefaultMerchantInput {
  pickup_name: string
  email: string
  address: string
  province_id: number
  district_id: number
  sub_district_id: number
  postcode: string
  mobile: string
}

export interface ReplaceDefaultMerchantResponse {
  status: string
  data: SpeedMerchantItem
}

export interface TpMerchantItem {
  id: string
  factory_id: string
  is_active: boolean
  is_default: boolean
  is_delete: boolean
  merchant_id: string
  merchant_name: string
  merchant_address: string
  merchant_distinct: string   // sub_district_name
  merchant_amphur: string     // district_name
  merchant_province: string
  merchant_postcode: string
  merchant_phone_number: string
  merchant_email: string
  postcode_drop: string
}

export interface GetTpMerchantListResponse {
  status: string
  data: TpMerchantItem[]
}

/** 2026-09-14: ใช้ตอน submit หน้า /manage-merchant ตอนยังไม่มี merchant เลยทั้งคู่ — สร้าง Speed +
 * ไปรษณีย์ไทยพร้อมกันในคำขอเดียว ชื่อจุดรับสินค้าไม่ให้กรอกเอง ส่งเป็น "maindc" คงที่เสมอ (ดู
 * ManageMerchantForm.tsx — field ซ่อนไว้ ไม่โชว์ให้ผู้ใช้เห็น) */
export interface CreateMerchantPairInput {
  pickup_name: string
  email: string
  address: string
  province_id: number
  district_id: number
  sub_district_id: number
  postcode: string
  mobile: string
}

export interface CreateMerchantPairResult {
  speed_status: 'success' | 'failed' | 'pending'
  speed: SpeedMerchantItem | null
  speed_error: string | null
  tp_status: 'success' | 'failed' | 'pending'
  tp: TpMerchantItem | null
  tp_error: string | null
}

export interface CreateMerchantPairResponse {
  status: string
  data: CreateMerchantPairResult
}

// --------------------------------------------------------
// API Functions
// --------------------------------------------------------

/** ดึงรายการ Speed Merchant ของโรงงาน */
export const getMerchantList = async (factory_id: string): Promise<GetMerchantListResponse> => {
  const data: AxiosResponse<GetMerchantListResponse> = await httpClient
    .get(API.getMerchantList(factory_id))
    .catch((error) => handleResponseError(error))
  return data.data
}

/** แทนที่ Default Merchant ด้วยข้อมูลใหม่ (สร้าง Merchant ใหม่กับ Speed + Deactivate เดิม) */
export const replaceDefaultMerchant = async (
  factory_id: string,
  input: ReplaceDefaultMerchantInput,
): Promise<ReplaceDefaultMerchantResponse> => {
  const data: AxiosResponse<ReplaceDefaultMerchantResponse> = await httpClient
    .post(API.replaceDefaultMerchant(factory_id), input)
    .catch((error) => handleResponseError(error))
  return data.data
}

/** ดึงรายการ Thailand Post Merchant ของโรงงาน */
export const getTpMerchantList = async (factory_id: string): Promise<GetTpMerchantListResponse> => {
  const data: AxiosResponse<GetTpMerchantListResponse> = await httpClient
    .get(API.getTpMerchantList(factory_id))
    .catch((error) => handleResponseError(error))
  return data.data
}

/** สร้าง Speed merchant + Thailand Post merchant พร้อมกัน — ใช้ตอนยังไม่มี merchant เลยทั้งคู่เท่านั้น
 * (backend เช็คซ้ำอีกชั้น ปฏิเสธถ้ามี merchant ใดฝั่งใดฝั่งหนึ่งอยู่แล้ว) */
export const createMerchantPair = async (
  factory_id: string,
  input: CreateMerchantPairInput,
): Promise<CreateMerchantPairResponse> => {
  const data: AxiosResponse<CreateMerchantPairResponse> = await httpClient
    .post(API.createMerchantPair(factory_id), input)
    .catch((error) => handleResponseError(error))
  return data.data
}
